import { useRef } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICES } from "@/constants/services";
import { CONTACT } from "@/constants/contact";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { CarouselDots } from "@/components/CarouselDots";
import { SwipeHint } from "@/components/SwipeHint";
import { ServiceCardMedia } from "@/components/ServiceCardMedia";
import { usePublicSite } from "@/lib/publicSite";

// Computers & tablets: a grid in this order — wide = 2 columns (photo beside the text on wide
// screens ≥1280px), zig-zagging left/right. Fills 7 even rows with the "Don't see your job?" card last.
const LAYOUT = [
  ["pictures", "wide"], ["painting"], ["doors"], ["mounting", "wide"], ["sheetrock", "wide"], ["caulking"],
  ["shower-head"], ["trim", "wide"], ["tile", "wide"], ["fans"], ["countertop"], ["advocate"],
  ["lighting"], ["punch-list"], ["leak-repair"],
];
export const WIDE_CARDS = new Set(LAYOUT.filter(([, size]) => size === "wide").map(([id]) => id));
const ORDERED = [
  ...LAYOUT.map(([id, size]) => ({ ...SERVICES.find((s) => s.id === id), size })).filter((s) => s.id),
  ...SERVICES.filter((s) => !LAYOUT.some(([id]) => id === s.id)),
];
const SIZE = {
  wide: { card: "md:col-span-8 xl:flex-row", media: "h-64 sm:h-80 xl:h-auto xl:w-[55%] xl:min-h-[26rem] xl:shrink-0", body: "min-w-0 xl:p-7 xl:justify-center", title: "text-2xl sm:text-3xl" },
  regular: { card: "md:col-span-4", media: "h-48", body: "", title: "text-xl sm:text-2xl" },
};

const ServiceCard = ({ s, i, isMobile, onBook, media, reveal }) => {
  const z = SIZE[s.size] || SIZE.regular;
  return (
    <motion.article
      data-testid={`service-card-${s.id}`}
      data-size={s.size || "regular"}
      data-book-cta={isMobile ? "" : undefined}
      {...reveal(i)}
      onClick={isMobile ? () => onBook(s.name) : undefined}
      className={`group cursor-pointer md:cursor-auto snap-center shrink-0 w-[80%] md:w-auto first:ml-[10%] last:mr-[10%] md:first:ml-0 md:last:mr-0 col-span-12 bg-card border border-border overflow-hidden flex flex-col hover:border-navy/40 hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow,border-color] duration-300 ${z.card}`}
    >
      <div className={`relative overflow-hidden ${z.media}`}>
        <ServiceCardMedia s={s} media={media} />
        <span className="absolute z-10 top-4 left-4 bg-navy text-white font-mono text-xs md:text-[10px] lg:text-xs whitespace-nowrap uppercase tracking-[0.15em] px-3 py-1.5" data-testid={`service-price-${s.id}`}>
          {s.price} · 2-hr minimum
        </span>
      </div>
      <div className={`p-6 flex flex-col flex-1 ${z.body}`}>
        <h3 className={`min-w-0 font-display font-bold text-navy tracking-tight ${z.title}`}>{s.name}</h3>
        <p className="text-sm text-muted-foreground mt-2">{s.tagline}</p>
        <ul className={`mt-4 space-y-1.5 ${s.size === "wide" ? "" : "flex-1"}`}>
          {s.features.map((f) => (
            <li key={f} className="text-sm text-foreground flex items-start gap-2">
              <span className="h-1 w-1 mt-2 bg-terracotta inline-block shrink-0" /> {f}
            </li>
          ))}
        </ul>
        <button
          data-testid={`service-book-${s.id}`}
          data-book-cta
          onClick={(e) => { e.stopPropagation(); onBook(s.name); }}
          className="mt-6 self-start inline-flex items-center gap-2 text-sm font-semibold text-navy group-hover:text-terracotta transition-colors"
        >
          Book this service
          <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </button>
      </div>
    </motion.article>
  );
};

export const Services = ({ onBook }) => {
  const site = usePublicSite();
  // Mobile shows the cards as a horizontal swipe carousel; the whileInView fade-in is disabled
  // there so off-screen cards in the scroller aren't left stuck invisible. Desktop is unchanged.
  const isMobile = useIsMobile();
  const scrollerRef = useRef(null);
  const cardReveal = (i) =>
    isMobile
      ? {}
      : {
          initial: { opacity: 0, y: 32 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, margin: "-60px" },
          transition: { duration: 0.6, delay: (i % 3) * 0.1 },
        };
  return (
    <section id="services" data-testid="services-section" className="py-16 sm:py-24 lg:py-32">
    <div className="px-4 sm:px-8 lg:px-16 mx-auto max-w-7xl">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="mb-12 sm:mb-16"
      >
        <p className="overline-label mb-4">What I fix & install</p>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-navy max-w-2xl">
          One booking. One guy. Every trade.
        </h2>
        <p className="mt-4 text-muted-foreground leading-relaxed max-w-2xl" data-testid="services-bundle-line">
          Short job? Bundle a few into one visit and the two hours go further.
        </p>
      </motion.div>

      <div className="relative">
      <div
        ref={scrollerRef}
        data-testid="services-scroller"
        className="flex md:grid grid-cols-12 md:grid-flow-row-dense gap-6 md:gap-8 overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none items-start md:items-stretch pb-2 md:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {ORDERED.map((s, i) => (
          <ServiceCard key={s.id} s={s} i={i} isMobile={isMobile} onBook={onBook} media={site?.service_media?.[s.id]} reveal={cardReveal} />
        ))}

        <motion.article
          data-testid="service-card-anything"
          {...cardReveal(SERVICES.length)}
          className="group snap-center shrink-0 w-[80%] md:w-auto first:ml-[10%] last:mr-[10%] md:first:ml-0 md:last:mr-0 col-span-12 md:col-span-4 bg-navy text-white p-6 flex flex-col hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300"
        >
          <div>
            <p className="overline-label">Odd jobs welcome</p>
            <h3 className="font-display text-xl sm:text-2xl font-bold tracking-tight mt-3">
              Don't see your job? Just ask.
            </h3>
            <p className="text-sm text-white/70 mt-2 leading-relaxed">
              If it involves a tool and a house, odds are I do it. Text me a photo of the problem
              and I'll tell you straight whether I'm your guy — and if it's beyond me, I'll point
              you to the right local pro. 25 years remodeling in Kitsap means I know them all.
            </p>
          </div>
          <div className="mt-6 flex flex-col items-start gap-3">
            <button
              data-testid="service-book-anything"
              data-book-cta
              onClick={() => onBook("Something else")}
              className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white text-sm font-semibold px-5 h-11 transition-colors"
            >
              Book an estimate visit
              <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
            <a
              href={CONTACT.smsHref}
              data-testid="service-anything-call"
              className="text-xs text-white/60 underline decoration-dotted underline-offset-4 hover:text-white transition-colors"
            >
              or text a photo
            </a>
          </div>
        </motion.article>
      </div>
      <SwipeHint storageKey="shs_swipe_hint_services" scrollerRef={scrollerRef} testid="services-carousel" />
      </div>
      <CarouselDots scrollerRef={scrollerRef} count={SERVICES.length + 1} testid="services-carousel" />
    </div>
    </section>
  );
};
